import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch } from "react-redux";
import findIndex from "lodash/findIndex";
import findLastIndex from "lodash/findLastIndex";
import Input, { InputProps } from "@material-ui/core/Input";
import TextField from "@material-ui/core/TextField";
import { FormHelperTextProps } from "@material-ui/core";

export interface Props extends InputProps {
  mask?: string;
  onTextChange?: (value: string) => void;
  outlined?: boolean;
  label?: string;
  variant?: "outlined" | "standard" | "filled";
  helperText?: string;
  InputProps?: Partial<InputProps>;
  FormHelperTextProps?: FormHelperTextProps;
}

export enum MaskPartType {
  Mask,
  Numbers
}

interface MaskPart {
  type: MaskPartType;
  chars: string;
}

const NUMBER_MASK_CHAR = "#";
const NUMBER_CHARS = "()0123456789";
const waitTimeout = requestAnimationFrame || setTimeout;

const NumberInput = (props: Props) => {
  const {
    mask = "",
    onTextChange,
    outlined,
    label,
    onFocus,
    onBlur,
    error,
    id,
    name,
    variant,
    helperText,
    InputProps,
    FormHelperTextProps,
    ...restProps
  } = props;
  const dispatch = useDispatch();
  const [value, setValue] = useState<string>("");
  const ref = useRef<React.MutableRefObject<any>>(null);
  const inputRef = (restProps.inputRef as React.MutableRefObject<any>) || ref;
  const input = inputRef?.current;

  const inputProps = restProps || {};

  Object.assign(inputProps, {
    inputProps: {
      ...restProps.inputProps,
      inputMode: "numeric"
    }
  });

  const regexParts = [];
  const maskParts = [];
  let regex: string;

  if (mask) {
    let numberChars = "";
    let maskChars = "";

    const saveMaskChars = () => {
      if (maskChars.length) {
        const maskPart: MaskPart = {
          type: MaskPartType.Mask,
          chars: maskChars
        };

        maskParts.push(maskPart);

        const regexPart = `[^0-9]{0,${maskChars.length}}`;

        regexParts.push(`(${regexPart})?`);
        maskChars = "";
      }
    };

    const saveNumChars = () => {
      if (numberChars.length) {
        const maskPart: MaskPart = {
          type: MaskPartType.Numbers,
          chars: numberChars
        };

        maskParts.push(maskPart);

        const regexPart = `[0-9]{1,${numberChars.length}}`;

        regexParts.push(`(${regexPart})?`);
        numberChars = "";
      }
    };

    for (const maskChar of mask) {
      if (maskChar === NUMBER_MASK_CHAR) {
        saveMaskChars();
        numberChars += NUMBER_MASK_CHAR;
      } else {
        saveNumChars();
        maskChars += maskChar;
      }
    }

    saveMaskChars();
    saveNumChars();

    regex = `^${regexParts.join("")}$`;
  } else {
    regex = `^([0-9]+)$`;
  }

  const handleKeyDown = (event: React.KeyboardEvent) => {
    const selectionStart = input?.selectionStart ?? -1;
    const selectionEnd = input?.selectionEnd ?? -1;
    const hasSelection = selectionStart > -1 && selectionEnd > -1;

    if (!input || !value || !hasSelection || selectionStart !== selectionEnd) {
      return;
    }

    if (event.key === "Backspace") {
      const leftChars = value.substring(0, selectionStart);
      const lastChar = leftChars.substring(selectionStart - 1);

      if (!NUMBER_CHARS.includes(lastChar)) {
        const lastNumberIndex = findLastIndex(leftChars, char => {
          return NUMBER_CHARS.includes(char);
        });

        if (lastNumberIndex > -1) {
          event.preventDefault();

          const newSelectionStart = lastNumberIndex;
          const leftText = value.substring(0, newSelectionStart);
          const rightText = value.substring(newSelectionStart + 1);
          const newValue = leftText + rightText;

          setFormattedValue(newValue);

          waitTimeout(() => {
            input.setSelectionRange(newSelectionStart, newSelectionStart);
          });
        }
      }
    } else if (event.key === "Delete") {
      const rightChars = value.substring(selectionStart);
      const firstChar = rightChars.substring(0, 1);

      if (!NUMBER_CHARS.includes(firstChar)) {
        const firstNumberIndex = findIndex(rightChars, char => {
          return NUMBER_CHARS.includes(char);
        });

        if (firstNumberIndex > -1) {
          event.preventDefault();

          const newSelectionStart = selectionStart + firstNumberIndex;
          const leftText =
            value.substring(0, selectionStart) +
            value.substring(selectionStart, newSelectionStart);
          const rightText = value.substring(newSelectionStart + 1);
          const newValue = leftText + rightText;

          setFormattedValue(newValue);

          waitTimeout(() => {
            input.setSelectionRange(newSelectionStart, newSelectionStart);
          });
        }
      }
    }
  };

  const setFormattedValue = (newValue = "") => {
    if (newValue === value) {
      return;
    }

    if (!newValue) {
      setValue(newValue);

      if (onTextChange) {
        onTextChange(newValue);
      }

      return;
    }

    const match = newValue.match(regex);

    if (match) {
      const matchParts = match.slice(1);
      let formattedValue = "";

      if (mask) {
        const lastNumberMatchIndex = findLastIndex(
          matchParts,
          (matchPart, index) => {
            const maskPart = maskParts[index];

            return !!matchPart && maskPart.type === MaskPartType.Numbers;
          }
        );

        let shouldWriteMask = true;
        let hasNumbers = false;

        for (const [i, matchPart] of matchParts.entries()) {
          const maskPart = maskParts[i];

          if (maskPart.type === MaskPartType.Mask) {
            if (shouldWriteMask || i < lastNumberMatchIndex) {
              formattedValue += maskPart.chars;
              shouldWriteMask = false;
            }
          } else {
            if (matchPart) {
              hasNumbers = true;
              formattedValue += matchPart;
              shouldWriteMask = matchPart.length === maskPart.chars.length;
            }
          }
        }

        if (!hasNumbers) {
          formattedValue = "";
        }
      } else {
        formattedValue = newValue;
      }

      setValue(formattedValue);

      if (onTextChange) {
        onTextChange(formattedValue);
      }
    }
  };

  const handleFocus = useCallback(
    event => {
      if (onFocus) {
        onFocus(event);
      }
    },
    [dispatch]
  );

  const handleBlur = useCallback(
    event => {
      if (onBlur) {
        onBlur(event);
      }
    },
    [dispatch]
  );

  const handleChange = (event: React.ChangeEvent<{ value: string }>) => {
    const newValue = event.target.value;

    setFormattedValue(newValue);
  };

  useEffect(() => {
    setFormattedValue(restProps?.value as string);
  }, [restProps.value]);

  return outlined ? (
    <TextField
      inputRef={inputRef}
      value={value}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
      error={error}
      label={label}
      id={id}
      name={name}
      variant={variant}
      helperText={helperText}
      autoComplete="off"
      InputProps={InputProps}
      FormHelperTextProps={FormHelperTextProps}
    />
  ) : (
    <Input
      {...restProps}
      id={id}
      inputRef={inputRef}
      value={value}
      onKeyDown={handleKeyDown}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onChange={handleChange}
    />
  );
};

export default NumberInput;
